/**
 * First window for a person with no projects: connect the coding agent
 * Story 586: Web sign-up tells new users to connect their coding agent
 *
 * The parent records the step as finished; this window only reports how it was left.
 */

import { useCallback, useEffect } from 'react';
import { ConnectAgentInstructions } from './ConnectAgentInstructions';
import './ConnectAgentModal.css';

interface ConnectAgentModalProps {
  onClose: () => void;
  onAddRepository: () => void;
}

export function ConnectAgentModal({ onClose, onAddRepository }: ConnectAgentModalProps) {
  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <div
      className="connect-step__backdrop"
      onClick={handleBackdropClick}
      data-testid="connect-step"
    >
      <div className="connect-step" role="dialog" aria-labelledby="connect-step-title">
        <h2 id="connect-step-title" className="connect-step__title">
          One more step: connect Ceetrix to your coding agent
        </h2>
        <ConnectAgentInstructions lead="Open a terminal inside one of your repositories and run:" />
        <p className="connect-step__text">
          This links the MCP server to this account. Restart your coding agent afterwards.
        </p>
        <p className="connect-step__urgent">
          Do this now. Ceetrix does nothing until your agent is connected.
        </p>
        <div className="connect-step__actions">
          <button
            type="button"
            className="connect-step__close"
            onClick={onClose}
            data-testid="connect-step-close"
          >
            Close
          </button>
          <button
            type="button"
            className="connect-step__add-repository"
            onClick={onAddRepository}
            data-testid="connect-step-add-repository"
          >
            Already connected? Add a repository here
          </button>
        </div>
      </div>
    </div>
  );
}
